import { Loader2, Plus, UserMinus, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";

import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { DataListCardField } from "@/components/shared/DataListCardField";
import { DataListCardRow } from "@/components/shared/DataListCardRow";
import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { ResponsiveListSplit } from "@/components/shared/ResponsiveListSplit";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import {
  type EnterpriseInvite,
  type EnterpriseMember,
  type EnterpriseRole,
  inviteEnterpriseMember,
  listEnterpriseInvites,
  listEnterpriseMembers,
  revokeEnterpriseInvite,
  revokeEnterpriseMember,
} from "@/services/platformEnterprisesService";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COLUMN_COUNT = 6;

type MemberRow = { type: "member"; data: EnterpriseMember };
type InviteRow = { type: "invite"; data: EnterpriseInvite };
type CombinedRow = MemberRow | InviteRow;

function getMemberDisplayName(m: EnterpriseMember): string {
  const parts = [m.user.firstName, m.user.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return m.user.email;
}

function roleLabel(role: EnterpriseRole, t: (k: string) => string): string {
  if (role === "enterprise_admin") return t("platformEnterprises.roleAdmin");
  return t("platformEnterprises.roleReviewer");
}

interface EnterpriseMembersTabProps {
  enterpriseId: string;
}

export function EnterpriseMembersTab({
  enterpriseId,
}: EnterpriseMembersTabProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();

  const MEMBERS_KEY = ["platform", "enterprises", enterpriseId, "members"] as const;
  const INVITES_KEY = ["platform", "enterprises", enterpriseId, "invites"] as const;

  const membersQuery = useQuery({
    queryKey: MEMBERS_KEY,
    queryFn: () => listEnterpriseMembers(enterpriseId),
  });

  const invitesQuery = useQuery({
    queryKey: INVITES_KEY,
    queryFn: () => listEnterpriseInvites(enterpriseId),
  });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<EnterpriseRole>("enterprise_admin");
  const [inviteEmailError, setInviteEmailError] = useState<string | null>(null);
  const [revokeMemberId, setRevokeMemberId] = useState<string | null>(null);
  const [revokeMemberName, setRevokeMemberName] = useState("");
  const [revokeInviteId, setRevokeInviteId] = useState<string | null>(null);
  const [revokeInviteEmail, setRevokeInviteEmail] = useState("");

  const members = membersQuery.data ?? [];
  const invites = invitesQuery.data ?? [];
  const loading = membersQuery.isLoading || invitesQuery.isLoading;
  const isRefreshing = membersQuery.isFetching || invitesQuery.isFetching;

  const combinedRows: CombinedRow[] = useMemo(() => {
    const memberRows: MemberRow[] = members.map((m) => ({ type: "member", data: m }));
    const inviteRows: InviteRow[] = invites.map((inv) => ({ type: "invite", data: inv }));
    return [...memberRows, ...inviteRows];
  }, [members, invites]);

  useEffect(() => {
    pagination.setTotal(combinedRows.length);
  }, [combinedRows.length, pagination]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return combinedRows.slice(start, start + pagination.limit);
  }, [combinedRows, pagination.page, pagination.limit]);

  const inviteMutation = useMutation({
    mutationFn: (payload: { email: string; role: EnterpriseRole }) =>
      inviteEnterpriseMember(enterpriseId, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITES_KEY });
      toast.success(t("platformEnterprises.inviteSent"));
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("enterprise_admin");
    },
    onError: () => {
      toast.error(t("platformEnterprises.inviteError"));
    },
  });

  const revokeMemberMutation = useMutation({
    mutationFn: (membershipId: string) =>
      revokeEnterpriseMember(enterpriseId, membershipId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEMBERS_KEY });
      toast.success(t("platformEnterprises.memberRevoked"));
      setRevokeMemberId(null);
    },
    onError: () => {
      toast.error(t("platformEnterprises.revokeError"));
    },
  });

  const revokeInviteMutation = useMutation({
    mutationFn: (invId: string) =>
      revokeEnterpriseInvite(enterpriseId, invId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: INVITES_KEY });
      toast.success(t("platformEnterprises.inviteRevoked"));
      setRevokeInviteId(null);
    },
    onError: () => {
      toast.error(t("platformEnterprises.revokeError"));
    },
  });

  const handleInviteSubmit = useCallback((): void => {
    if (!EMAIL_RE.test(inviteEmail.trim())) {
      setInviteEmailError(t("platformEnterprises.validationEmailFormat"));
      return;
    }
    setInviteEmailError(null);
    inviteMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  }, [inviteEmail, inviteRole, inviteMutation, t]);

  const handleRefresh = (): void => {
    void membersQuery.refetch();
    void invitesQuery.refetch();
  };

  const buildMemberActions = useCallback(
    (m: EnterpriseMember): TableAction[] => [
      {
        label: t("platformEnterprises.revokeMemberAction"),
        icon: UserMinus,
        variant: "destructive",
        onClick: () => {
          setRevokeMemberId(m.id);
          setRevokeMemberName(getMemberDisplayName(m));
        },
      },
    ],
    [t],
  );

  const buildInviteActions = useCallback(
    (inv: EnterpriseInvite): TableAction[] => [
      {
        label: t("platformEnterprises.revokeInviteAction"),
        icon: XCircle,
        variant: "destructive",
        onClick: () => {
          setRevokeInviteId(inv.id);
          setRevokeInviteEmail(inv.email);
        },
      },
    ],
    [t],
  );

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead>{t("platformEnterprises.colMemberName")}</TableHead>
        <TableHead>{t("platformEnterprises.colMemberEmail")}</TableHead>
        <TableHead>{t("platformEnterprises.colRole")}</TableHead>
        <TableHead>{t("platformEnterprises.colStatus")}</TableHead>
        <TableHead>{t("platformEnterprises.colDate")}</TableHead>
        <TableHead className="w-[80px]">{t("platformEnterprises.colActions")}</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button
          type="button"
          onClick={() => setInviteOpen(true)}
          className="transition-colors duration-150"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("platformEnterprises.inviteButton")}
        </Button>
      </div>

      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : (
          <>
            {combinedRows.length === 0 ? (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} className="h-32">
                          <EmptyState
                            title={t("platformEnterprises.noMembers")}
                            message={t("platformEnterprises.noMembersMessage")}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="flex min-h-[40vh] items-center justify-center p-4">
                    <EmptyState
                      title={t("platformEnterprises.noMembers")}
                      message={t("platformEnterprises.noMembersMessage")}
                    />
                  </div>
                }
              />
            ) : (
              <ResponsiveListSplit
                table={
                  <Table>
                    {tableHeader}
                    <TableBody>
                      {paginatedRows.map((row) =>
                        row.type === "member" ? (
                          <TableRow key={`member-${row.data.id}`}>
                            <TableCell className="font-medium text-foreground">
                              {getMemberDisplayName(row.data)}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.user.email}
                            </TableCell>
                            <TableCell>
                              <StatusBadge variant="info">
                                {roleLabel(row.data.role, t)}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>
                              <StatusBadge variant="success">
                                {t("platformEnterprises.statusActive")}
                              </StatusBadge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.acceptedAt
                                ? formatDisplayDate(row.data.acceptedAt)
                                : "—"}
                            </TableCell>
                            <TableCell>
                              <TableActionsMenu
                                actions={buildMemberActions(row.data)}
                              />
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={`invite-${row.data.id}`}>
                            <TableCell className="font-medium">—</TableCell>
                            <TableCell className="text-muted-foreground">
                              {row.data.email}
                            </TableCell>
                            <TableCell>
                              <StatusBadge variant="info">
                                {roleLabel(row.data.role, t)}
                              </StatusBadge>
                            </TableCell>
                            <TableCell>
                              <StatusBadge variant="warning">
                                {t("platformEnterprises.statusPendingInvite")}
                              </StatusBadge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {formatDisplayDate(row.data.expiresAt)}
                            </TableCell>
                            <TableCell>
                              <TableActionsMenu
                                actions={buildInviteActions(row.data)}
                              />
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                }
                cards={
                  <div className="divide-y divide-border">
                    {paginatedRows.map((row) =>
                      row.type === "member" ? (
                        <DataListCardRow key={`member-${row.data.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                              {getMemberDisplayName(row.data)}
                            </p>
                            <div className="shrink-0">
                              <TableActionsMenu
                                actions={buildMemberActions(row.data)}
                              />
                            </div>
                          </div>
                          <div className="mt-3 space-y-3">
                            <DataListCardField
                              label={t("platformEnterprises.colMemberEmail")}
                            >
                              <span className="text-muted-foreground">
                                {row.data.user.email}
                              </span>
                            </DataListCardField>
                            <DataListCardField
                              label={t("platformEnterprises.colRole")}
                            >
                              <StatusBadge variant="info">
                                {roleLabel(row.data.role, t)}
                              </StatusBadge>
                            </DataListCardField>
                            <DataListCardField
                              label={t("platformEnterprises.colStatus")}
                            >
                              <StatusBadge variant="success">
                                {t("platformEnterprises.statusActive")}
                              </StatusBadge>
                            </DataListCardField>
                          </div>
                        </DataListCardRow>
                      ) : (
                        <DataListCardRow key={`invite-${row.data.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <p className="min-w-0 flex-1 font-semibold leading-snug text-foreground">
                              {row.data.email}
                            </p>
                            <div className="shrink-0">
                              <TableActionsMenu
                                actions={buildInviteActions(row.data)}
                              />
                            </div>
                          </div>
                          <div className="mt-3 space-y-3">
                            <DataListCardField
                              label={t("platformEnterprises.colRole")}
                            >
                              <StatusBadge variant="info">
                                {roleLabel(row.data.role, t)}
                              </StatusBadge>
                            </DataListCardField>
                            <DataListCardField
                              label={t("platformEnterprises.colStatus")}
                            >
                              <StatusBadge variant="warning">
                                {t("platformEnterprises.statusPendingInvite")}
                              </StatusBadge>
                            </DataListCardField>
                            <DataListCardField
                              label={t("platformEnterprises.colDate")}
                            >
                              <span className="text-muted-foreground">
                                {formatDisplayDate(row.data.expiresAt)}
                              </span>
                            </DataListCardField>
                          </div>
                        </DataListCardRow>
                      ),
                    )}
                  </div>
                }
              />
            )}
            {combinedRows.length > 0 && (
              <TablePagination
                pagination={pagination}
                rowsPerPageLabel={t("platformEnterprises.rowsPerPage")}
                pageOfLabel={t("platformEnterprises.pageOf", {
                  from: (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                })}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
              />
            )}
          </>
        )}
      </TableCard>

      {/* Invite dialog */}
      <Dialog
        open={inviteOpen}
        onOpenChange={(open) => {
          if (!inviteMutation.isPending) {
            setInviteOpen(open);
            if (!open) {
              setInviteEmail("");
              setInviteEmailError(null);
            }
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("platformEnterprises.inviteDialogTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">
                {t("platformEnterprises.fieldInviteEmail")}
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteEmailError(null);
                }}
                disabled={inviteMutation.isPending}
                placeholder="member@company.com"
              />
              {inviteEmailError != null && (
                <p className="text-xs text-destructive">{inviteEmailError}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-role">
                {t("platformEnterprises.fieldInviteRole")}
              </Label>
              <Select
                value={inviteRole}
                onValueChange={(v) => setInviteRole(v as EnterpriseRole)}
                disabled={inviteMutation.isPending}
              >
                <SelectTrigger id="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enterprise_admin">
                    {t("platformEnterprises.roleAdmin")}
                  </SelectItem>
                  <SelectItem value="enterprise_reviewer">
                    {t("platformEnterprises.roleReviewer")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setInviteOpen(false)}
              disabled={inviteMutation.isPending}
            >
              {t("pages.common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleInviteSubmit}
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("platformEnterprises.inviteSending")}
                </>
              ) : (
                t("platformEnterprises.inviteSend")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={revokeMemberId != null}
        onOpenChange={(open) => {
          if (!open) setRevokeMemberId(null);
        }}
        title={t("platformEnterprises.revokeMemberTitle")}
        description={t("platformEnterprises.revokeMemberDescription", {
          name: revokeMemberName,
        })}
        confirmText={t("platformEnterprises.revokeConfirm")}
        cancelText={t("pages.common.cancel")}
        loadingText={t("platformEnterprises.revoking")}
        variant="destructive"
        isLoading={revokeMemberMutation.isPending}
        onConfirm={() => {
          if (revokeMemberId) revokeMemberMutation.mutate(revokeMemberId);
        }}
      />

      <ConfirmationDialog
        open={revokeInviteId != null}
        onOpenChange={(open) => {
          if (!open) setRevokeInviteId(null);
        }}
        title={t("platformEnterprises.revokeInviteTitle")}
        description={t("platformEnterprises.revokeInviteDescription", {
          email: revokeInviteEmail,
        })}
        confirmText={t("platformEnterprises.revokeConfirm")}
        cancelText={t("pages.common.cancel")}
        loadingText={t("platformEnterprises.revoking")}
        variant="destructive"
        isLoading={revokeInviteMutation.isPending}
        onConfirm={() => {
          if (revokeInviteId) revokeInviteMutation.mutate(revokeInviteId);
        }}
      />
    </>
  );
}
